import { store } from "~/state/store";
import { CONFIG } from "~/config";
import { v4 as uuidv4 } from "uuid";
import { LocalCache } from "~/util/cache";
import { watch, watchCurrentUser } from "~/state/util";
import { IUserInfo } from "~/state/user/interfaces";
import { IDescriptionOutput, description } from "./poiagg";

interface IGetHistoryParams {
	limit: number;
	lang: Array<string>;
	since?: number;
	to?: number;
}

interface IHistoryItem {
	createTime: string;
	hits?: Array<{
		time: string;
	}>;
	id: number;
	title: string;
	type: string;
	updateTime: string;
	data?: {
		entityId: number;
		entitySource: string;
		lat: number;
		lon: number;
	}
}

export type THistoryItemDescription = IHistoryItem & { secondRow?: string }

interface IHistoryResponse {
	items: Array<IHistoryItem>;
}

interface IHistoryResponseError {
	detail: Array<{
		msg: string;
		type: string;
	}>;
}

export interface IHistoryItemData {
	title: string;
	source?: string;
	createTime?: string;
	type: "poi" | "query" | "category" | "destination" | "destination_mm";
	data?: {
		entitySource?: string;
		entityId?: number;
		lon?: number;
		lat?: number;
		lang?: string;
		mmId?: string;
	};
	mark?: {
		lat: number;
		lon: number;
	};
}

const URL = "/history/history/";
const URL_SUGGEST_CATEGORIES = "/history/suggest/menu";
const CACHE_KEY = "userHistoryItems";
const Cache = new LocalCache({
	ttl: 2000,
});

if (CONFIG.userHistory?.items?.length) {
	Cache.put(CACHE_KEY + 3, Promise.resolve({
		data: {
			items: CONFIG.userHistory.items,
		},
	}));
}

const watchUser = watch(store.getState, "user", watchCurrentUser);

store.subscribe(watchUser(() => {
	Cache.flush();
}));

export function getHistory(user: IUserInfo, options: IGetHistoryParams = {
	limit: 30,
	lang: CONFIG.langPriority,
}): Promise<IHistoryResponse> {
	if (user.state !== "green") {
		return Promise.resolve({ items: [] });
	}
	const params = options;
	let fromCache = Cache.get(CACHE_KEY + options.limit);

	if (!fromCache) {
		const requestPromise = Mapy.Util.request(`${CONFIG.requestUrl}${URL}`, { get: params, type: "json" }).promise;

		Cache.put(CACHE_KEY + options.limit, requestPromise);
		fromCache = requestPromise;
	}

	return fromCache;
}

export function saveHistory(user: IUserInfo, inputData: Array<IHistoryItemData> | IHistoryItemData): Promise<string | IHistoryResponseError> {
	if (user.state !== "green") {
		return Promise.resolve(`{ "detail": "Not logged in" }`);
	}

	const data = Array.isArray(inputData) ? inputData : [inputData];
	const date = new Date();

	data.forEach(item => {
		item.createTime = `${date.getUTCFullYear()}-${(date.getUTCMonth()+1).toString().padStart(2, "0")}-${date.getUTCDate().toString().padStart(2, "0")}T${date.getUTCHours().toString().padStart(2, "0")}:${date.getUTCMinutes().toString().padStart(2, "0")}:${date.getUTCSeconds().toString().padStart(2, "0")}Z`;
	});

	Cache.flush();

	return Mapy.Util.request(`${CONFIG.requestUrl}${URL}`, { method: "POST", json: data, type: "json" }).promise;
}

export function removeHistory(user: IUserInfo): Promise<string | IHistoryResponseError> {
	if (user.state !== "green") {
		return Promise.resolve(`{ "detail": "Not logged in" }`);
	}

	Cache.flush();

	return Mapy.Util.request(`${CONFIG.requestUrl}${URL}`, { method: "DELETE", get: { all: true }, type: "json" }).promise;
}

export function removeHistoryItem(user: IUserInfo, data: IHistoryItemData): Promise<string | IHistoryResponseError> {
	if (user.state !== "green") {
		return Promise.resolve(`{ "detail": "Not logged in" }`);
	}

	Cache.flush();

	return Mapy.Util.request(`${CONFIG.requestUrl}${URL}`, { method: "DELETE", json: [data], type: "json" }).promise;
}

export function getCategories() {
	if (CONFIG.requestUrl) {
		let lang = "";

		CONFIG.langPriority.forEach((language, index) => {
			lang += `${index > 0 ? `&` : ``}lang=${language}`;
		});

		return fetch(`${CONFIG.requestUrl}${URL_SUGGEST_CATEGORIES}?${lang}`, {
			method: "GET",
			headers: {
				"Accept": "application/json",
				"Content-Type": "application/json",
				"X-Correlation-Id": uuidv4(),
			},
			credentials: "include",
		});
	}

	return Promise.reject();
}

export async function enrichHistoryWithDescription(items: Array<IHistoryItem>): Promise<Array<THistoryItemDescription>> {
	let result: Array<THistoryItemDescription> = [...items];

	if (result.length) {
		try {
			const promises = [];
			const pois = [];

			for (const poi of result) {
				if (poi.data?.entitySource && poi.data?.entitySource !== "coor") {
					const fromCache = Cache.get(`history-item-description-${poi.data?.entitySource}${poi.data?.entityId}`);

					if (fromCache) {
						promises.push(fromCache.then((response: IDescriptionOutput) => {
							const respResult = {};
							const curId = `${poi.data.entitySource}${poi.data.entityId}`;

							(response?.poi || []).forEach(respPoi => {
								respResult[`${respPoi.source}${respPoi.id}`] = respPoi;
							});

							if (curId in respResult) {
								const secondRow = [respResult[curId]?.titleVars?.specification, respResult[curId]?.titleVars?.locationMain1].join(", ");

								if (poi && secondRow) {
									poi.secondRow = secondRow;
								}
							}

							return response;
						}));
					} else {
						pois.push({
							source: poi.data.entitySource,
							id: poi.data.entityId,
						});
					}
				}
			}

			if (pois.length) {
				const request = description(pois, {
					fetchPhoto: true,
					ratios: ["1x1"],
					lang: CONFIG.langPriority,
					query: "",
					wikimedia: true,
				}).then(response => {
					const descResults = response.result;

					result = result.map(poi => ({
						...poi,
						...poi.data?.entitySource
							&& poi.data?.entitySource !== "coor"
							&& `${poi.data.entitySource}${poi.data.entityId}` in descResults
							? { secondRow: [descResults[`${poi.data.entitySource}${poi.data.entityId}`]?.titleVars?.specification, descResults[`${poi.data.entitySource}${poi.data.entityId}`]?.titleVars?.locationMain1].join(", ") }
							: {},
					}));

					return response;
				}, () => {});

				pois.forEach(item => {
					if (request) {
						Cache.put(`history-item-description-${item.source}${item.id}`, request, 10000);
					}
				});

				promises.push(request);
			}

			await Promise.allSettled(promises);

			return Promise.resolve(result);
		} catch (ex) {
			/* eslint-disable-next-line */
			console.log(ex);

			// pokud to z jakehokoliv duvodu selze, vratime itemy jak byly a tedy nebudou mit druhy radek, ale budou tam
			return Promise.resolve(result);
		}
	} else {
		return Promise.resolve(result);
	}
}
